import { useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type { MatchSummary } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { useMatches } from "@/features/matches/useMatches";
import { CoachModal } from "./CoachModal";
import { CheckList } from "./CheckList";
import { useCoachNotesList } from "../hooks/useCoachNotesList";
import { useCoachClipsList } from "../hooks/useCoachClipsList";
import { useCoachPlaylistsList } from "../hooks/useCoachPlaylistsList";
import type { MatchSummaryMutations } from "../hooks/useCoachSummariesList";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Match-summary composer modal — Coach > Match Summaries.
 *
 * Ports the legacy `openCoachMatchSummaryModal`
 * (`js/coaching/match-summaries.js`). `null` `summary` ⇒ create;
 * a `MatchSummary` ⇒ edit.
 *
 * Fields mirror `CreateMatchSummaryRequest`:
 *   - match (LOCKED on edit — a summary's match is immutable;
 *     legacy disabled the select with a hint)
 *   - visibility
 *   - team_positives / team_improvements / training_focus / body
 *     — the recap text fields
 *   - note_ids / clip_ids / playlist_ids — linked evidence,
 *     picked from checklists. Privacy: the BACKEND filters
 *     private linked sources out of viewer payloads — the
 *     composer just lets a coach attach them; it never exposes
 *     them itself.
 *
 * Like the playlist composer, this is its own modal — there's no
 * Coach Review surface for match summaries.
 */

// Match summaries have no `player_ids` column, and
// `filter_match_summaries_for_user` only ever surfaces `team` /
// `unlisted` summaries to viewers — a `player`-visibility summary
// would be invisible to the family it's meant for. Drop that
// option here so the composer can't promise visibility the
// backend never delivers. `private` stays: it's a legit
// coach-only state (a summary that intentionally doesn't reach
// viewers), same as a draft.
const VISIBILITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "private", label: "Coach-only (private)" },
  { value: "team", label: "Team" },
  { value: "unlisted", label: "Unlisted" },
];

export type MatchSummaryComposerModalProps = {
  summary: MatchSummary | null;
  mutations: MatchSummaryMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function MatchSummaryComposerModal({
  summary,
  mutations,
  onClose,
  onSaved,
}: MatchSummaryComposerModalProps) {
  const isEdit = summary !== null;
  const toast = useToast();
  const matchesQuery = useMatches();
  const notesQuery = useCoachNotesList();
  const clipsQuery = useCoachClipsList();
  const playlistsQuery = useCoachPlaylistsList();

  const matches = matchesQuery.data ?? [];
  const [matchId, setMatchId] = useState(
    summary?.match_id ?? matches[0]?.id ?? "",
  );
  // A summary persisted before `player` was dropped would have no
  // matching <option>; coerce it to `team` (the closest viewer-
  // facing intent) so the select doesn't render a blank value.
  const [visibility, setVisibility] = useState<string>(() => {
    const raw = summary?.visibility ?? "private";
    return raw === "player" ? "team" : raw;
  });
  const [teamPositives, setTeamPositives] = useState(
    summary?.team_positives ?? "",
  );
  const [teamImprovements, setTeamImprovements] = useState(
    summary?.team_improvements ?? "",
  );
  const [trainingFocus, setTrainingFocus] = useState(
    summary?.training_focus ?? "",
  );
  const [body, setBody] = useState(summary?.body ?? "");
  const [noteIds, setNoteIds] = useState<Set<number>>(
    () => new Set(summary?.note_ids ?? []),
  );
  const [clipIds, setClipIds] = useState<Set<number>>(
    () => new Set(summary?.clip_ids ?? []),
  );
  const [playlistIds, setPlaylistIds] = useState<Set<number>>(
    () => new Set(summary?.playlist_ids ?? []),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If matches load after mount, default the select to the first.
  const resolvedMatchId =
    matchId || (isEdit ? (summary?.match_id ?? "") : matches[0]?.id ?? "");

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matches) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matches]);

  const toggle = <V extends number>(
    setter: React.Dispatch<React.SetStateAction<Set<V>>>,
  ) => (id: V) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvedMatchId) {
      setError("Pick a match for this summary.");
      return;
    }
    // The backend rejects an all-blank summary (the merged-state
    // validator); block it inline so the coach gets a clear
    // message instead of a 422.
    const hasText = [teamPositives, teamImprovements, trainingFocus, body].some(
      (t) => t.trim(),
    );
    if (!hasText) {
      setError("Add at least one recap section before saving.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit) {
        // The match is immutable — the PATCH body omits `match_id`.
        await mutations.updateSummary(summary.id, {
          visibility,
          team_positives: teamPositives.trim(),
          team_improvements: teamImprovements.trim(),
          training_focus: trainingFocus.trim(),
          body: body.trim(),
          note_ids: [...noteIds],
          clip_ids: [...clipIds],
          playlist_ids: [...playlistIds],
        });
      } else {
        await mutations.createSummary({
          match_id: resolvedMatchId,
          visibility,
          team_positives: teamPositives.trim(),
          team_improvements: teamImprovements.trim(),
          training_focus: trainingFocus.trim(),
          body: body.trim(),
          note_ids: [...noteIds],
          clip_ids: [...clipIds],
          playlist_ids: [...playlistIds],
        });
      }
      toast.success(isEdit ? "Match summary updated" : "Match summary created");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : `Could not ${isEdit ? "update" : "create"} the summary.`,
      );
      toast.error(`Couldn't ${isEdit ? "update" : "create"} match summary`, {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const notes = notesQuery.data?.notes ?? [];
  const clips = clipsQuery.data?.clips ?? [];
  const playlists = playlistsQuery.data?.playlists ?? [];

  return (
    <CoachModal
      title={isEdit ? "Edit match summary" : "New match summary"}
      onClose={onClose}
      data-testid="summary-composer-modal"
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <p className="text-ink-muted text-xs">
          Team-visible summaries appear in My Feedback. Linked private
          notes / clips / playlists are filtered server-side — they're
          never exposed to viewers.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Match">
            <TkticSelect
              value={resolvedMatchId}
              onChange={(next) => setMatchId(next)}
              disabled={isEdit || matchesQuery.isLoading}
              data-testid="summary-composer-match"
              ariaLabel="Match"
              options={
                matches.length === 0
                  ? [{ value: "", label: "No matches yet" }]
                  : matches.map((m) => ({
                      value: m.id,
                      label: matchLabelById.get(m.id) ?? m.id,
                    }))
              }
            />
          </Field>
          <Field label="Visibility">
            <TkticSelect
              value={visibility}
              onChange={(next) => setVisibility(next)}
              data-testid="summary-composer-visibility"
              ariaLabel="Visibility"
              options={VISIBILITY_OPTIONS.map((v) => ({
                value: v.value,
                label: v.label,
              }))}
            />
          </Field>
        </div>
        {isEdit ? (
          <p className="text-ink-muted text-xs -mt-1">
            Match can't be changed — create a new summary to recap a
            different match.
          </p>
        ) : null}

        <Field label="Team positives">
          <textarea
            value={teamPositives}
            onChange={(e) => setTeamPositives(e.target.value)}
            data-testid="summary-composer-positives"
            rows={2}
            placeholder="What went well as a team?"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>
        <Field label="Areas to improve">
          <textarea
            value={teamImprovements}
            onChange={(e) => setTeamImprovements(e.target.value)}
            data-testid="summary-composer-improvements"
            rows={2}
            placeholder="What should we clean up next match?"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>
        <Field label="Training focus">
          <textarea
            value={trainingFocus}
            onChange={(e) => setTrainingFocus(e.target.value)}
            data-testid="summary-composer-training"
            rows={2}
            placeholder="Next practice focus"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>
        <Field label="Coach recap" helper="Optional full recap for the team.">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="summary-composer-body"
            rows={3}
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        {/* Evidence — link notes / clips / playlists. */}
        <Field label={`Linked notes (${noteIds.size})`}>
          <CheckList
            data-testid="summary-composer-notes"
            emptyLabel="No notes yet"
            items={notes.map((n) => ({
              value: n.id,
              label:
                n.title?.trim() ||
                n.event_title?.trim() ||
                "(untitled note)",
              selected: noteIds.has(n.id),
            }))}
            onToggle={toggle(setNoteIds)}
          />
        </Field>
        <Field label={`Linked clips (${clipIds.size})`}>
          <CheckList
            data-testid="summary-composer-clips"
            emptyLabel="No clips yet"
            items={clips.map((c) => ({
              value: c.id,
              label: `${formatPlayhead(c.start_seconds)}–${formatPlayhead(
                c.end_seconds,
              )} · ${c.title?.trim() || "(untitled clip)"}`,
              selected: clipIds.has(c.id),
            }))}
            onToggle={toggle(setClipIds)}
          />
        </Field>
        <Field label={`Linked playlists (${playlistIds.size})`}>
          <CheckList
            data-testid="summary-composer-playlists"
            emptyLabel="No playlists yet"
            items={playlists.map((p) => ({
              value: p.id,
              label: p.title?.trim() || "(untitled playlist)",
              selected: playlistIds.has(p.id),
            }))}
            onToggle={toggle(setPlaylistIds)}
          />
        </Field>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="summary-composer-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          data-testid="summary-composer-submit"
          className="bg-accent hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted text-white rounded px-4 py-2 text-sm font-display uppercase tracking-wide transition-colors"
        >
          {submitting ? "Saving…" : isEdit ? "Save changes" : "Create summary"}
        </button>
      </form>
    </CoachModal>
  );
}

function Field(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
    </label>
  );
}
